import type { SectionId } from './nav-items';
import type { NavIconName } from './nav-icon';
import type { MessageKey } from '@bookingdei/i18n/message-key';

// Where an admin inbox row leads and how it looks (design `notifGo` + NOTIF_CATS
// icon / tone / action, D:8540-8570, superadmin-data.js NOTIF_CATS). The contract row carries `entity_ref` as "<kind>:<id>"
// and a free-form `category` (DB-65); both are mapped here, in one named place,
// so the bell tray and the Notifications section agree. Unknown kinds fall back to
// the Notifications section itself (AP-D10 — tolerate unknown values).

const KIND_SECTION: Record<string, SectionId> = {
  booking: 'bookings',
  business: 'businesses',
  catalog_item: 'approvals',
  claim: 'approvals',
  application: 'approvals',
  dispute: 'disputes',
  refund: 'payments',
  subscription_payment: 'payments',
  dsr: 'customers',
  user: 'customers',
  offer: 'offers',
  review: 'reviews',
};

const CATEGORY_SECTION: Record<string, SectionId> = {
  proofs: 'bookings',
  kyc: 'approvals',
  service: 'approvals',
  offer: 'offers',
  review: 'reviews',
  refund: 'payments',
  dispute: 'disputes',
  dispute_sla: 'disputes',
  sub_failed: 'payments',
  merchant_state: 'businesses',
  sla: 'approvals',
  health: 'overview',
  pdpa: 'customers',
};

// Design tones: amber → warn, red → danger, neutral → neutral (notifTone D:15190).
export type NotificationTone = 'info' | 'warn' | 'danger' | 'neutral';

const CATEGORY_LOOK: Record<string, { icon: NavIconName; tone: NotificationTone; action: MessageKey }> = {
  proofs: { icon: 'card', tone: 'warn', action: 'admin.nf.action.verify' },
  kyc: { icon: 'store', tone: 'neutral', action: 'admin.nf.action.track' },
  service: { icon: 'layers', tone: 'warn', action: 'admin.nf.action.fix' },
  offer: { icon: 'tag', tone: 'warn', action: 'admin.nf.action.review' },
  review: { icon: 'star', tone: 'warn', action: 'admin.nf.action.moderate' },
  refund: { icon: 'swap', tone: 'neutral', action: 'admin.nf.action.pay_out' },
  dispute: { icon: 'scale', tone: 'neutral', action: 'admin.nf.action.open' },
  dispute_sla: { icon: 'scale', tone: 'danger', action: 'admin.nf.action.review' },
  sub_failed: { icon: 'currency', tone: 'danger', action: 'admin.nf.action.chase' },
  merchant_state: { icon: 'lock', tone: 'danger', action: 'admin.nf.action.open' },
  sla: { icon: 'shield', tone: 'danger', action: 'admin.nf.action.triage' },
  health: { icon: 'chart', tone: 'danger', action: 'admin.nf.action.check' },
  pdpa: { icon: 'user', tone: 'neutral', action: 'admin.nf.action.handle' },
  support: { icon: 'chat', tone: 'neutral', action: 'admin.nf.action.open' },
};

/** The section an inbox row opens, before the viewer's grants are applied. */
export function notificationSection(n: { entity_ref?: string; category: string }): SectionId {
  const kind = n.entity_ref?.split(':', 1)[0];
  return (kind && KIND_SECTION[kind]) || CATEGORY_SECTION[n.category] || 'notifications';
}

export function notificationLook(category: string): { icon: NavIconName; tone: NotificationTone; action: MessageKey } {
  return CATEGORY_LOOK[category] ?? { icon: 'bell', tone: 'neutral', action: 'admin.nf.action.open' };
}
